import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getCoord } from '@turf/invariant';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import type { Feature, MultiPolygon, Polygon } from '@turf/helpers';
import distance from '@turf/distance';
import bbox from '@turf/bbox';
import center from '@turf/center';
import * as helpers from '@turf/helpers';
import lineIntersect from '@turf/line-intersect';
import centroid from '@turf/centroid';

import {
  activeItemChanged,
  selectActiveItem,
} from '../../../../pages/item-details/store';
import { compilePath } from '../../../../common/utils/helpers/routes';
import { selectStyle } from '../../../store';
import { useGlMap } from '../../../contexts/context';
import { ITEM_TYPES } from '../../../../common/utils/constants/constants';
import { getItemType } from '../../../../common/utils/helpers/item';
import { reverseSearch } from '../../../../common/services/api';
import { sendReportToSentry } from '../../../../common/utils/helpers/sentry';
import InteractiveMapLayer from '../interactive-map-layer/InteractiveMapLayer';
import { getNearestPointOnPolygon } from '../../../utils/helpers/utils';

import { getLayerStyleData, sourceId, sourceLayer } from './BuildingLayerStyle';

/**
 * This method returns coordinates of a visual center of a building geojson feature
 *
 * Please check the following examples of buildings with non-standard shapes:
 *   * https://www.mapbox.com/contribute/#/?l=18.17%2F38.7691%2F-9.1779
 *   * https://www.mapbox.com/contribute/#/?l=18.2981%2F38.7425%2F-9.1453&q=
 *   * mapbox.com/contribute/#/?l=17.6864%2F38.7364%2F-9.127&q=
 */
const getBuildingVisualCenterPoint = (buildingFeature) => {
  const buildingMathCenter = centroid(buildingFeature.geometry);

  // try to find a nearest point on the polygon to use it to create a line on which visual center is expected to be
  const nearestPointOnPolygon = getNearestPointOnPolygon(
    buildingFeature,
    buildingMathCenter,
  );

  if (!nearestPointOnPolygon) {
    return getCoord(buildingMathCenter);
  }

  const buildingBbox = bbox(buildingFeature);
  // true length of a line to cover all the polygon is >= than diagonal of the bbox of the polygon
  // x2 length of the longest side is a simplification which is >= than the diagonal
  // it is a half of the extended line because the center of mass (which is a center of a line) may be on the edge
  // of the polygon and there is no sense to calculate exact length, just to cover the polygon
  const extendedLineHalfLength =
    Math.max(
      buildingBbox[2] - buildingBbox[0],
      buildingBbox[3] - buildingBbox[1],
    ) * 2;

  const directionVector = helpers.point([
    getCoord(nearestPointOnPolygon)[0] - getCoord(buildingMathCenter)[0],
    getCoord(nearestPointOnPolygon)[1] - getCoord(buildingMathCenter)[1],
  ]);
  const directionVectorLength = Math.sqrt(
    getCoord(directionVector)[0] ** 2 + getCoord(directionVector)[1] ** 2,
  );
  const normalizedDirectionVector = helpers.point([
    getCoord(directionVector)[0] / directionVectorLength,
    getCoord(directionVector)[1] / directionVectorLength,
  ]);

  // in case center of mass is on the building - we need to have two-sided line to get two points,
  // othervise only one-sided ray is needed

  let extendedLine;
  if (booleanPointInPolygon(buildingMathCenter, buildingFeature)) {
    extendedLine = helpers.lineString([
      [
        getCoord(buildingMathCenter)[0] -
          getCoord(normalizedDirectionVector)[0] * extendedLineHalfLength,
        getCoord(buildingMathCenter)[1] -
          getCoord(normalizedDirectionVector)[1] * extendedLineHalfLength,
      ],
      [
        getCoord(buildingMathCenter)[0] +
          getCoord(normalizedDirectionVector)[0] * extendedLineHalfLength,
        getCoord(buildingMathCenter)[1] +
          getCoord(normalizedDirectionVector)[1] * extendedLineHalfLength,
      ],
    ]);
  } else {
    extendedLine = helpers.lineString([
      getCoord(buildingMathCenter),
      [
        getCoord(buildingMathCenter)[0] +
          getCoord(normalizedDirectionVector)[0] * extendedLineHalfLength,
        getCoord(buildingMathCenter)[1] +
          getCoord(normalizedDirectionVector)[1] * extendedLineHalfLength,
      ],
    ]);
  }

  const intersectionPoints = lineIntersect(buildingFeature, extendedLine);

  if (!intersectionPoints.features.length) {
    return getCoord(nearestPointOnPolygon || buildingMathCenter);
  }

  if (intersectionPoints.features.length === 1) {
    return getCoord(helpers.point(getCoord(intersectionPoints.features[0])));
  }

  // order points by distance, so pick only 2 closest
  intersectionPoints.features = intersectionPoints.features.sort((f1, f2) => {
    const dist1 = distance(f1, buildingMathCenter);
    const dist2 = distance(f2, buildingMathCenter);

    return dist2 - dist1;
  });

  return getCoord(
    center(
      helpers.points([
        getCoord(intersectionPoints.features[0]),
        getCoord(intersectionPoints.features[1]),
      ]),
    ),
  );
};

const BuildingLayer = () => {
  const map = useGlMap();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentMapStyle = useSelector(selectStyle);
  const { style: layerStyle, layerId } = useMemo(
    () => getLayerStyleData(currentMapStyle.id),
    [currentMapStyle],
  );

  const item = useSelector(selectActiveItem);
  const selectedFeature = useMemo(
    () => (getItemType(item) === ITEM_TYPES.address ? item : null),
    [item],
  );
  const [selectedFeatureId, setSelectedFeatureId] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (!selectedFeature) {
      setSelectedFeatureId(null);

      return;
    }

    if (selectedFeature.properties.sourceLayerFeatureId) {
      setSelectedFeatureId(selectedFeature.properties.sourceLayerFeatureId);

      return;
    }

    // In case feature coming not from the map, but from Search API.
    const findSelectedFeatureOnRender = () => {
      const featureLocationPoint = selectedFeature.geometry;
      if (!map.getLayer(layerId)) {
        setSelectedFeatureId(null);
        return;
      }

      const features = map
        .queryRenderedFeatures(undefined, {
          layers: [layerId],
        })
        .filter((feature) =>
          booleanPointInPolygon(
            featureLocationPoint,
            feature as unknown as Feature<Polygon | MultiPolygon>,
          ),
        );
      const featureId = features?.[0]?.id || null;

      setSelectedFeatureId(featureId);

      if (featureId) {
        map.off('render', findSelectedFeatureOnRender);
      }
    };

    map.on('render', findSelectedFeatureOnRender);
    return () => {
      map.off('render', findSelectedFeatureOnRender);
    };
  }, [map, selectedFeature, layerId]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const onFeatureClick = useCallback(
    async (mapFeature) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const reverseSearchBuildingFeatureType = 'address';

      // const buildingMassCenter = centroidPoint;
      const buildingVisualCenter = getBuildingVisualCenterPoint(mapFeature);
      try {
        // as long as building layer doesn't have any useful information about the address, so we have to use it's
        // geometry and request the address from the reverse search API
        const feature = await reverseSearch({
          lng: buildingVisualCenter[0],
          lat: buildingVisualCenter[1],
          types: reverseSearchBuildingFeatureType,
          abortSignal: abortController.signal,
        });

        if (
          !feature ||
          !feature.properties.place_type.includes(
            reverseSearchBuildingFeatureType,
          )
        ) {
          return;
        }

        dispatch(
          activeItemChanged({
            item: {
              ...feature,
              geometry: {
                ...feature.geometry,
                coordinates: buildingVisualCenter,
              },
              properties: {
                ...feature.properties,
                sourceLayerFeatureId: mapFeature.id,
              },
            },
          }),
        );

        history.push({
          pathname: compilePath('details'),
          search: history.location.search,
        });
      } catch (error) {
        if (!abortController.signal.aborted) {
          sendReportToSentry(error, {
            origin: 'Buildings layer address search',
          });
        }
      } finally {
        abortControllerRef.current = null;
      }
    },
    [dispatch, history],
  );

  return (
    <>
      <InteractiveMapLayer
        sourceId={sourceId}
        sourceLayer={sourceLayer}
        layerId={layerId}
        layerStyle={layerStyle}
        selectedFeatureId={selectedFeatureId}
        onFeatureClick={onFeatureClick}
      />
    </>
  );
};

export default BuildingLayer;
